import { Box, Divider, Grid2 as Grid } from "@mui/material";
import { Button, SelectBox, TextField } from "../../common";
import { useAtom } from "jotai";
import { isCreateLocationComponentActiveAtom } from "../../../atoms";
import { useValidateForm } from "../../../hooks";
import { getCreateLocationSchema } from "../../../validations";
import { AccountLocation } from "../../../types";
import { useMemo, useState } from "react";
import { getCountryConfig } from "../../../utils";

type CreateLocationProps = {
  enableSave?: boolean;
  defaultValues?: AccountLocation;
  handleCreate: (
    location: Omit<AccountLocation, "locationNumber" | "country">,
  ) => void;
  handleDiscard?: () => void;
};

export const CreateLocation = ({
  enableSave = false,
  defaultValues,
  handleCreate,
  handleDiscard,
}: CreateLocationProps) => {
  const isCanada = defaultValues?.country === "CA";

  const { stateLabel, zipLabel, statesDropdown } = getCountryConfig(
    defaultValues?.country,
  );

  const schema = useMemo(() => {
    return getCreateLocationSchema(isCanada);
  }, [isCanada]);

  const [, setLocationCreateComponentActive] = useAtom(
    isCreateLocationComponentActiveAtom,
  );
  const [hasChanges, setHasChanges] = useState(enableSave);

  const { register, handleSubmit, errors, reset } = useValidateForm(schema);

  const handleClickDiscard = () => {
    setLocationCreateComponentActive(false);
  };

  const handleChange = () => {
    setHasChanges(true);
  };

  return (
    <Box
      onSubmit={handleSubmit(handleCreate)}
      component={"form"}
      noValidate
      sx={{
        height: "100%",
      }}
    >
      <Grid
        container
        size={12}
        direction={"row"}
        gap={2}
        sx={{
          boxShadow: "0px 0px 4px 0px #00000040",
          borderRadius: "10px",
          p: 4,
          mb: 3,
        }}
      >
        <Grid
          container
          size={12}
          spacing={3.5}
          gap={2}
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Location Name"
              name="locationName"
              register={register}
              error={errors["locationName"]}
              defaultValue={defaultValues?.locationName}
              onChange={handleChange}
              maxLength={50}
              required
            />
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <TextField
              label="Address Line 1"
              name="addressLine1"
              register={register}
              error={errors["addressLine1"]}
              defaultValue={defaultValues?.addressLine1}
              onChange={handleChange}
              maxLength={50}
              required
            />
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <TextField
              label="Address Line 2 (Optional)"
              name="addressLine2"
              register={register}
              error={errors["addressLine2"]}
              defaultValue={defaultValues?.addressLine2}
              onChange={handleChange}
              maxLength={50}
            />
          </Grid>
        </Grid>
        <Grid
          container
          size={12}
          spacing={2.5}
          gap={2}
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Grid size={{ sm: 4, xs: 12 }}>
            <TextField
              label="City"
              name="city"
              register={register}
              error={errors["city"]}
              defaultValue={defaultValues?.city}
              onChange={handleChange}
              maxLength={50}
              required
            />
          </Grid>
          <Grid size={{ sm: 4, xs: 12 }}>
            <SelectBox
              register={register}
              defaultValue={defaultValues?.state}
              label={stateLabel}
              name="state"
              dropdown={statesDropdown}
              error={errors["state"]}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid size={{ sm: 4, xs: 12 }}>
            <TextField
              type="text"
              label={zipLabel}
              name="zip"
              register={register}
              error={errors["zip"]}
              defaultValue={defaultValues?.zip}
              onChange={handleChange}
              maxLength={10}
              required
            />
          </Grid>
        </Grid>
        <Grid
          container
          size={12}
          spacing={3}
          gap={2}
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Grid size={{ sm: 12, xs: 12 }}>
            <TextField
              label="Instructions"
              name="instruction"
              multiline
              rows={4}
              register={register}
              error={errors["instruction"]}
              defaultValue={defaultValues?.instruction}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
        <Grid container size={12} spacing={4}>
          <Divider sx={{ width: "100%" }} />
        </Grid>
        <Grid
          container
          size={12}
          spacing={4}
          gap={2}
          sx={{
            justifyContent: { lg: "flex-end", xs: "space-between" },
          }}
        >
          <Grid size={{ lg: 3, sm: 6, xs: 12 }}>
            <Button
              variantType="secondary"
              label="Discard"
              sx={{
                padding: "0.5rem",
                width: "100%",
              }}
              onClick={() => {
                reset();
                setHasChanges(false);
                handleDiscard ? handleDiscard() : handleClickDiscard();
              }}
            />
          </Grid>
          <Grid size={{ lg: 3, sm: 6, xs: 12 }}>
            <Button
              type={hasChanges ? "submit" : undefined}
              disabled={!hasChanges}
              label="Save Location"
              variantType={hasChanges ? "primary" : "secondary"}
              sx={{
                padding: "0.5rem",
                width: "100%",
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
